import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Minus, X, Upload, Save, FileText, AlertTriangle } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select';
import { ScrollArea } from '../ui/scroll-area';
import { ImageUpload } from '../upload/ImageUpload';
import { generateId, IMAGE_CONSTRAINTS } from '../../lib/utils';
import { templatesApi, duplicateCheckApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import debounce from 'lodash.debounce';

const CATEGORIES = [
    { id: 'relics', name: 'Relics' },
    { id: 'accessories', name: 'Accessories' },
    { id: 'skills', name: 'Skills' },
    { id: 'tutorials', name: 'Tutorials' },
];

const RARITIES = [
    { id: 'Common', name: 'Common', color: 'text-gray-400' },
    { id: 'Uncommon', name: 'Uncommon', color: 'text-green-400' },
    { id: 'Rare', name: 'Rare', color: 'text-blue-400' },
    { id: 'Epic', name: 'Epic', color: 'text-purple-400' },
    { id: 'Legendary', name: 'Legendary', color: 'text-amber-400' },
    { id: 'Mythical', name: 'Mythical', color: 'text-red-400' },
    { id: 'Monarch', name: 'Monarch', color: 'text-sky-400' },
    { id: 'Limited', name: 'Limited', color: 'text-pink-400' },
    { id: 'ULimited', name: 'ULimited', color: 'text-violet-400' },
];

export const CreateEditModal = ({ 
    open, 
    onOpenChange, 
    onSubmit, 
    category: defaultCategory,
    entry = null,
    mode = 'create' 
}) => {
    const { user } = useAuth();
    const [templates, setTemplates] = useState([]);
    const [showTemplates, setShowTemplates] = useState(false);
    const [templateName, setTemplateName] = useState('');
    const [duplicateWarning, setDuplicateWarning] = useState(null);
    const [checkingDuplicate, setCheckingDuplicate] = useState(false);

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        category: defaultCategory,
        image_url: '',
        rarity: '',
        is_meta: false,
        is_received: false,
        dynamic_fields: [],
	attributes: [],
        ranks: [],
        tutorial_steps: [],
    });

    const isTutorial = formData.category === 'tutorials';
    const isAccessory = formData.category === 'accessories';
    const isRelic = formData.category === 'relics';
    const isSkill = formData.category === 'skills';
    const showRarityAndMeta = isAccessory || isRelic || isSkill;

    // Load templates for the category
    useEffect(() => {
        if (open && formData.category) {
            loadTemplates(formData.category);
        }
    }, [open, formData.category]);

    const loadTemplates = async (category) => {
        try {
            const response = await templatesApi.getAll(category);
            setTemplates(response.data || []);
        } catch (error) {
            console.error('Failed to load templates:', error);
        }
    };

    // Duplicate check with debounce
    const checkDuplicate = useCallback(
        debounce(async (name, category, excludeId) => {
            if (!name || name.length < 3) {
                setDuplicateWarning(null);
                return;
            }
            setCheckingDuplicate(true);
            try {
                const response = await duplicateCheckApi.check(name, category, excludeId);
                if (response.data.has_similar) {
                    setDuplicateWarning(response.data);
                } else {
                    setDuplicateWarning(null);
                }
            } catch (error) {
                console.error('Failed to check duplicate:', error);
            } finally {
                setCheckingDuplicate(false);
            }
        }, 500),
        []
    );

    // Check for duplicates when title changes
    useEffect(() => {
        if (formData.title && formData.category) {
            checkDuplicate(formData.title, formData.category, entry?.id);
        }
    }, [formData.title, formData.category, entry?.id]);

    useEffect(() => {
        if (open) {
            if (entry && mode === 'edit') {
                setFormData({
                    title: entry.title || '',
                    description: entry.description || '',
                    category: entry.category || defaultCategory,
                    image_url: entry.image_url || '',
                    rarity: entry.rarity || '',
                    is_meta: entry.is_meta || false,
                    is_received: entry.is_received || false,
                    dynamic_fields: entry.dynamic_fields || [],
                    attributes: entry.attributes || [],
		    ranks: entry.ranks || [],
                    tutorial_steps: entry.tutorial_steps || [],
                });
            } else {
                setFormData({
                    title: '',
                    description: '',
                    category: defaultCategory,
                    image_url: '',
                    rarity: '',
                    is_meta: false,
                    is_received: false,
                    dynamic_fields: [],
		    attributes: [],
                    ranks: [],
                    tutorial_steps: [],
                });
            }
            setDuplicateWarning(null);
            setShowTemplates(false);
        }
    }, [open, entry, mode, defaultCategory]);

    const handleSubmit = (e) => {
        e.preventDefault();
        const submitData = {
            ...formData,
            rarity: formData.rarity || null,
        };
        onSubmit(submitData);
    };

    // Apply template
    const applyTemplate = (template) => {
        const data = template.template_data;
        setFormData(prev => ({
            ...prev,
            description: data.description || prev.description,
            rarity: data.rarity || prev.rarity,
            is_meta: data.is_meta || false,
            dynamic_fields: data.dynamic_fields || [],
	    attributes: data.attributes || [],
            ranks: data.ranks || [],
            tutorial_steps: data.tutorial_steps || [],
        }));
        setShowTemplates(false);
        toast.success(`Vorlage "${template.name}" angewendet`);
    };

    // Save as template
    const saveAsTemplate = async () => {
        if (!templateName.trim()) {
            toast.error('Bitte Vorlagen-Name eingeben');
            return;
        }
        try {
            const templateData = {
                name: templateName.trim(),
                category: formData.category,
                template_data: {
                    description: formData.description,
                    rarity: formData.rarity,
                    is_meta: formData.is_meta,
                    dynamic_fields: formData.dynamic_fields,
		    attributes: data.attributes || [],
                    ranks: formData.ranks,
                    tutorial_steps: formData.tutorial_steps,
                }
            };
            await templatesApi.create(templateData, user?.discord_id, user?.username);
            toast.success('Vorlage gespeichert!');
            setTemplateName('');
            loadTemplates(formData.category);
        } catch (error) {
            toast.error('Fehler beim Speichern der Vorlage');
        }
    };

    // Delete template
    const deleteTemplate = async (templateId, e) => {
        e.stopPropagation();
        try {
            await templatesApi.delete(templateId);
            toast.success('Vorlage gelöscht');
            loadTemplates(formData.category);
        } catch (error) {
            toast.error('Fehler beim Löschen');
        }
    };

    // Dynamic Fields handlers
    const addDynamicField = () => {
        setFormData(prev => ({
            ...prev,
            dynamic_fields: [...prev.dynamic_fields, { id: generateId(), name: '', value: '' }]
        }));
    };

    const removeDynamicField = (index) => {
        setFormData(prev => ({
            ...prev,
            dynamic_fields: prev.dynamic_fields.filter((_, i) => i !== index)
        }));
    };

    const updateDynamicField = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            dynamic_fields: prev.dynamic_fields.map((f, i) => 
                i === index ? { ...f, [field]: value } : f
            )
        }));
    };

     // Attribute handlers (Accessories)
    const addAttribute = () => {
        setFormData(prev => ({
            ...prev,
            attributes: [...(prev.attributes || []), { id: generateId(), name: '', value: '' }]
        }));
    };

    const removeAttribute = (index) => {
        setFormData(prev => ({
            ...prev,
            attributes: (prev.attributes || []).filter((_, i) => i !== index)
        }));
    };

    const updateAttribute = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            attributes: (prev.attributes || []).map((f, i) => 
                i === index ? { ...f, [field]: value } : f
            )
        }));
    };

    // Rank handlers
    const addRank = () => {
        setFormData(prev => ({
            ...prev,
            ranks: [...prev.ranks, { id: generateId(), rank_name: '', is_max: false, fields: [] }]
        }));
    };

    const removeRank = (index) => {
        setFormData(prev => ({
            ...prev,
            ranks: prev.ranks.filter((_, i) => i !== index)
        }));
    };

    const updateRankName = (index, name) => {
        setFormData(prev => ({
            ...prev,
            ranks: prev.ranks.map((r, i) => 
                i === index ? { ...r, rank_name: name } : r
            )
        }));
    };

    const updateRankIsMax = (index, isMax) => {
        setFormData(prev => ({
            ...prev,
            ranks: prev.ranks.map((r, i) => 
                i === index ? { ...r, is_max: isMax } : r
            )
        }));
    };

    const addRankField = (rankIndex) => {
        setFormData(prev => ({
            ...prev,
            ranks: prev.ranks.map((r, i) => 
                i === rankIndex 
                    ? { ...r, fields: [...r.fields, { id: generateId(), name: '', value: '' }] }
                    : r
            )
        }));
    };

    const removeRankField = (rankIndex, fieldIndex) => {
        setFormData(prev => ({
            ...prev,
            ranks: prev.ranks.map((r, i) => 
                i === rankIndex 
                    ? { ...r, fields: r.fields.filter((_, fi) => fi !== fieldIndex) }
                    : r
            )
        }));
    };

    const updateRankField = (rankIndex, fieldIndex, field, value) => {
        setFormData(prev => ({
            ...prev,
            ranks: prev.ranks.map((r, i) => 
                i === rankIndex 
                    ? { 
                        ...r, 
                        fields: r.fields.map((f, fi) => 
                            fi === fieldIndex ? { ...f, [field]: value } : f
                        )
                    }
                    : r
            )
        }));
    };

    // Tutorial Steps handlers
    const addTutorialStep = () => {
        setFormData(prev => ({
            ...prev,
            tutorial_steps: [...prev.tutorial_steps, { id: generateId(), title: '', content: '', image_url: '', video_url: '' }]
        }));
    };

    const removeTutorialStep = (index) => {
        setFormData(prev => ({
            ...prev,
            tutorial_steps: prev.tutorial_steps.filter((_, i) => i !== index)
        }));
    };

    const updateTutorialStep = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            tutorial_steps: prev.tutorial_steps.map((s, i) => 
                i === index ? { ...s, [field]: value } : s
            )
        }));
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[90vh] bg-[#0A0A0A] border-white/10">
                <DialogHeader>
                    <DialogTitle className="font-['Rajdhani'] text-2xl text-white">
                        {mode === 'create' ? 'Create New Entry' : 'Edit Entry'}
                    </DialogTitle>
                </DialogHeader>

                <ScrollArea className="max-h-[calc(90vh-180px)] pr-4">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Templates Section */}
                        {mode === 'create' && (
                            <div className="space-y-3 p-4 rounded-xl bg-purple-500/5 border border-purple-500/20">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <FileText size={18} className="text-purple-400" />
                                        <Label className="text-purple-400 font-semibold">Vorlagen</Label>
                                    </div>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setShowTemplates(!showTemplates)}
                                        className="text-purple-400 hover:bg-purple-500/10"
                                    >
                                        {showTemplates ? 'Ausblenden' : `Anzeigen (${templates.length})`}
                                    </Button>
                                </div>

                                {showTemplates && (
                                    <div className="space-y-2">
                                        {templates.length === 0 ? (
                                            <p className="text-sm text-gray-500 text-center py-2">Keine Vorlagen für diese Kategorie</p>
                                        ) : (
                                            templates.map((template) => (
                                                <div 
                                                    key={template.id}
                                                    onClick={() => applyTemplate(template)}
                                                    className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10 cursor-pointer hover:border-purple-500/30 transition-colors"
                                                >
                                                    <span className="text-white">{template.name}</span>
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={(e) => deleteTemplate(template.id, e)}
                                                        className="text-gray-500 hover:text-red-400 hover:bg-red-500/10 h-7 w-7"
                                                    >
                                                        <X size={14} />
                                                    </Button>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                )}

                                {/* Save as Template */}
                                <div className="flex gap-2 pt-2 border-t border-purple-500/20">
                                    <Input
                                        value={templateName}
                                        onChange={(e) => setTemplateName(e.target.value)}
                                        placeholder="Vorlagen-Name..."
                                        className="flex-1 bg-black/40 border-purple-500/30 text-sm"
                                    />
                                    <Button
                                        type="button"
                                        onClick={saveAsTemplate}
                                        size="sm"
                                        className="bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 border border-purple-500/30"
                                    >
                                        <Save size={14} className="mr-1" />
                                        Speichern
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* Basic Info */}
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="title" className="text-gray-400">Title *</Label>
                                <Input
                                    id="title"
                                    data-testid="entry-title-input"
                                    value={formData.title}
                                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                                    placeholder="Enter title"
                                    required
                                    className={cn(
                                        "bg-black/40 border-white/10",
                                        duplicateWarning?.has_exact && "border-red-500/50"
                                    )}
                                />
                                
                                {/* Duplicate Warning */}
                                {duplicateWarning && (
                                    <div className={cn(
                                        "flex items-start gap-2 p-3 rounded-lg text-sm",
                                        duplicateWarning.has_exact 
                                            ? "bg-red-500/10 border border-red-500/30 text-red-400"
                                            : "bg-amber-500/10 border border-amber-500/30 text-amber-400"
                                    )}>
                                        <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                                        <div>
                                            <p className="font-semibold">
                                                {duplicateWarning.has_exact 
                                                    ? 'Exakter Duplikat gefunden!'
                                                    : 'Ähnliche Namen gefunden:'}
                                            </p>
                                            <ul className="mt-1 space-y-0.5">
                                                {duplicateWarning.duplicates.map((dup, idx) => (
                                                    <li key={idx} className="text-xs opacity-80">
                                                        • {dup.name} ({dup.match_type === 'exact' ? 'exakt' : dup.match_type === 'similar' ? 'ähnlich' : 'enthält'})
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="category" className="text-gray-400">Category *</Label>
                                <Select
                                    value={formData.category}
                                    onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                                >
                                    <SelectTrigger data-testid="category-select" className="bg-black/40 border-white/10">
                                        <SelectValue placeholder="Select category" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#0A0A0A] border-white/10">
                                        {CATEGORIES.map((cat) => (
                                            <SelectItem key={cat.id} value={cat.id}>
                                                {cat.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Rarity Dropdown - für Relics, Accessories und Skills */}
                            {showRarityAndMeta && (
                                <div className="space-y-2">
                                    <Label htmlFor="rarity" className="text-gray-400">Rarity *</Label>
                                    <Select
                                        value={formData.rarity}
                                        onValueChange={(value) => setFormData(prev => ({ ...prev, rarity: value }))}
                                    >
                                        <SelectTrigger data-testid="rarity-select" className="bg-black/40 border-white/10">
                                            <SelectValue placeholder="Select rarity" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-[#0A0A0A] border-white/10 max-h-60 overflow-y-auto">
                                            {RARITIES.map((rarity) => (
                                                <SelectItem key={rarity.id} value={rarity.id}>
                                                    <span className={rarity.color}>{rarity.name}</span>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}

                            {/* Meta Checkbox */}
                            {showRarityAndMeta && (
                                <div className="flex items-center gap-4">
                                    <div className="flex items-center gap-2 p-3 rounded-lg bg-orange-500/10 border border-orange-500/30 flex-1">
                                        <input
                                            type="checkbox"
                                            id="is_meta"
                                            checked={formData.is_meta}
                                            onChange={(e) => setFormData(prev => ({ ...prev, is_meta: e.target.checked }))}
                                            className="w-5 h-5 rounded border-orange-500/50 bg-black/40 text-orange-500 focus:ring-orange-500"
                                        />
                                        <Label htmlFor="is_meta" className="text-orange-400 cursor-pointer">
                                            Meta
                                        </Label>
                                    </div>
                                </div>
                            )}

                            <div className="space-y-2">
                                <Label htmlFor="description" className="text-gray-400">Description</Label>
                                <Textarea
                                    id="description"
                                    data-testid="entry-description-input"
                                    value={formData.description}
                                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                    placeholder="Enter description"
                                    rows={3}
                                    className="bg-black/40 border-white/10 resize-none"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label className="text-gray-400">
                                    Bild
                                    <span className="text-xs text-gray-600 ml-2">(500x500px)</span>
                                </Label>
                                <ImageUpload
                                    currentImage={formData.image_url}
                                    onUpload={(url) => setFormData(prev => ({ ...prev, image_url: url || '' }))}
                                />
                                <div className="text-xs text-gray-500 mt-2">Oder URL direkt eingeben:</div>
                                <Input
                                    id="image_url"
                                    data-testid="entry-image-input"
                                    value={formData.image_url}
                                    onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                                    placeholder="https://example.com/image.jpg"
                                    className="bg-black/40 border-white/10"
                                />
                            </div>
                        </div>

                        {/* Dynamic Fields (nicht für Tutorials) */}
                        {!isTutorial && (
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <Label className="text-gray-400">Dynamic Fields</Label>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={addDynamicField}
                                        data-testid="add-field-button"
                                        className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10"
                                    >
                                        <Plus size={16} className="mr-1" />
                                        Add Field
                                    </Button>
                                </div>
                                
                                {formData.dynamic_fields.map((field, index) => (
                                    <div key={field.id || index} className="flex gap-2 items-start">
                                        <Input
                                            data-testid={`field-name-${index}`}
                                            value={field.name}
                                            onChange={(e) => updateDynamicField(index, 'name', e.target.value)}
                                            placeholder="Field name"
                                            className="flex-1 bg-black/40 border-white/10"
                                        />
                                        <Input
                                            data-testid={`field-value-${index}`}
                                            value={field.value}
                                            onChange={(e) => updateDynamicField(index, 'value', e.target.value)}
                                            placeholder="Value"
                                            className="flex-1 bg-black/40 border-white/10"
                                        />
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => removeDynamicField(index)}
                                            className="text-gray-400 hover:text-red-400 hover:bg-red-500/10 shrink-0"
                                        >
                                            <Minus size={16} />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        )}

			{/* Attributes (nur für Accessories) */}
                        {isAccessory && (
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <Label className="text-gray-400">Attributes</Label>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={addAttribute}
                                        data-testid="add-attribute-button"
                                        className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                                    >
                                        <Plus size={16} className="mr-1" />
                                        Add Attribute
                                    </Button>
                                </div>
                                
                                {(formData.attributes || []).map((field, index) => (
                                    <div key={field.id || index} className="flex gap-2 items-start">
                                        <Input
                                            data-testid={`attr-name-${index}`}
                                            value={field.name}
                                            onChange={(e) => updateAttribute(index, 'name', e.target.value)}
                                            placeholder="Attribute name"
                                            className="flex-1 bg-black/40 border-white/10"
                                        />
                                        <Input
                                            data-testid={`attr-value-${index}`}
                                            value={field.value}
                                            onChange={(e) => updateAttribute(index, 'value', e.target.value)}
                                            placeholder="Value"
                                            className="flex-1 bg-black/40 border-white/10"
                                        />
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => removeAttribute(index)}
                                            className="text-gray-400 hover:text-red-400 hover:bg-red-500/10 shrink-0"
                                        >
                                            <Minus size={16} />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Ranks (nur für Relics) */}
                        {isRelic && (
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <Label className="text-gray-400">Ranks</Label>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={addRank}
                                        data-testid="add-rank-button"
                                        className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                                    >
                                        <Plus size={16} className="mr-1" />
                                        Add Rank
                                    </Button>
                                </div>

                                {(formData.ranks || []).map((rank, rankIndex) => (
                                    <div 
                                        key={rank.id || rankIndex} 
                                        className="p-4 rounded-xl bg-white/5 border border-white/10 space-y-4"
                                    >
                                        <div className="flex gap-2 items-center">
                                            <Input
                                                data-testid={`rank-name-${rankIndex}`}
                                                value={rank.rank_name}
                                                onChange={(e) => updateRankName(rankIndex, e.target.value)}
                                                placeholder="Rank name (e.g., Legendary, Epic)"
                                                className="flex-1 bg-black/40 border-amber-500/30"
                                            />
                                            <label className="flex items-center gap-2 px-3 py-2 rounded-lg bg-black/40 border border-amber-500/30 cursor-pointer hover:bg-amber-500/10 transition-colors">
                                                <input
                                                    type="checkbox"
                                                    data-testid={`rank-max-${rankIndex}`}
                                                    checked={rank.is_max || false}
                                                    onChange={(e) => updateRankIsMax(rankIndex, e.target.checked)}
                                                    className="w-4 h-4 rounded border-amber-500/50 bg-black/40 text-amber-500 focus:ring-amber-500/50"
                                                />
                                                <span className="text-sm text-amber-400 font-medium whitespace-nowrap">MAX</span>
                                            </label>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => removeRank(rankIndex)}
                                                className="text-gray-400 hover:text-red-400 hover:bg-red-500/10"
                                            >
                                                <X size={16} />
                                            </Button>
                                        </div>

                                        {/* Rank Fields */}
                                        <div className="space-y-2 pl-4 border-l-2 border-amber-500/20">
                                            {(rank.fields || []).map((field, fieldIndex) => (
                                                <div key={field.id || fieldIndex} className="flex gap-2 items-start">
                                                    <Input
                                                        data-testid={`rank-${rankIndex}-field-name-${fieldIndex}`}
                                                        value={field.name}
                                                        onChange={(e) => updateRankField(rankIndex, fieldIndex, 'name', e.target.value)}
                                                        placeholder="Stat name"
                                                        className="flex-1 bg-black/40 border-white/10"
                                                    />
                                                    <Input
                                                        data-testid={`rank-${rankIndex}-field-value-${fieldIndex}`}
                                                        value={field.value}
                                                        onChange={(e) => updateRankField(rankIndex, fieldIndex, 'value', e.target.value)}
                                                        placeholder="Value"
                                                        className="flex-1 bg-black/40 border-white/10"
                                                    />
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => removeRankField(rankIndex, fieldIndex)}
                                                        className="text-gray-400 hover:text-red-400 hover:bg-red-500/10 shrink-0"
                                                    >
                                                        <Minus size={16} />
                                                    </Button>
                                                </div>
                                            ))}
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => addRankField(rankIndex)}
                                                className="text-cyan-400 hover:bg-cyan-500/10"
                                            >
                                                <Plus size={14} className="mr-1" />
                                                Add Stat
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Tutorial Steps (nur für Tutorials) */}
                        {isTutorial && (
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <Label className="text-gray-400">Tutorial Steps</Label>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={addTutorialStep}
                                        data-testid="add-step-button"
                                        className="border-green-500/30 text-green-400 hover:bg-green-500/10"
                                    >
                                        <Plus size={16} className="mr-1" />
                                        Add Step
                                    </Button>
                                </div>

                                {formData.tutorial_steps.map((step, index) => (
                                    <div 
                                        key={step.id || index} 
                                        className="p-4 rounded-xl bg-white/5 border border-white/10 space-y-3"
                                    >
                                        <div className="flex items-center gap-2">
                                            <span className="w-6 h-6 rounded-full bg-cyan-500/20 flex items-center justify-center text-cyan-400 text-sm font-mono">
                                                {index + 1}
                                            </span>
                                            <Input
                                                data-testid={`step-title-${index}`}
                                                value={step.title}
                                                onChange={(e) => updateTutorialStep(index, 'title', e.target.value)}
                                                placeholder="Step title"
                                                className="flex-1 bg-black/40 border-white/10"
                                            />
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => removeTutorialStep(index)}
                                                className="text-gray-400 hover:text-red-400 hover:bg-red-500/10"
                                            >
                                                <X size={16} />
                                            </Button>
                                        </div>
                                        <Textarea
                                            data-testid={`step-content-${index}`}
                                            value={step.content}
                                            onChange={(e) => updateTutorialStep(index, 'content', e.target.value)}
                                            placeholder="Step content..."
                                            rows={2}
                                            className="bg-black/40 border-white/10 resize-none"
                                        />
                                        <Input
                                            data-testid={`step-image-${index}`}
                                            value={step.image_url || ''}
                                            onChange={(e) => updateTutorialStep(index, 'image_url', e.target.value)}
                                            placeholder="Bild URL (optional)"
                                            className="bg-black/40 border-white/10"
                                        />
                                        <Input
                                            data-testid={`step-video-${index}`}
                                            value={step.video_url || ''}
                                            onChange={(e) => updateTutorialStep(index, 'video_url', e.target.value)}
                                            placeholder="Video URL (YouTube, Vimeo, MP4...)"
                                            className="bg-black/40 border-white/10"
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                    </form>
                </ScrollArea>

                <DialogFooter className="gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        className="border-white/10 text-gray-400"
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        onClick={handleSubmit}
                        data-testid="submit-entry-button"
                        className="bg-cyan-500 hover:bg-cyan-400 text-black font-semibold"
                    >
                        {mode === 'create' ? 'Create Entry' : 'Save Changes'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
